import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, useParams } from '@tanstack/react-router';
import { ArrowLeft, Truck } from 'lucide-react';
import type { ReactNode } from 'react';

import { useDetailTitle } from '@/components/page-header';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/lib/auth';
import { TASK_STATUS_META, type TaskStatus } from '@/lib/deliveries';
import { formatAddress, formatRelative, formatStoreDateTime } from '@/lib/order-display';
import { FALLBACK_TIMEZONE, getStoreTimeZone } from '@/lib/orders';
import { supabase } from '@/lib/supabase';

/**
 * One delivery task: its summary, the event trail, and proof of delivery when
 * a driver app has left one. Proof is read-only here -- there is no capture
 * surface in this build (dispatcher-only), so the section says so rather than
 * pretending.
 */
export const Route = createFileRoute('/deliveries/$deliveryId')({
  component: DeliveryDetailPage,
});

async function loadTask(accountId: string, taskId: string) {
  const [task, events, proof] = await Promise.all([
    supabase
      .from('delivery_tasks')
      .select(
        `id, status, eta, started_at, arrived_at, completed_at, failed_at, failure_reason,
         created_at, destination_address, destination_latitude, destination_longitude,
         drivers(name, phone),
         order:orders(id, order_number, status, total_cents, customer_snapshot,
           customers(first_name, last_name, phone))`,
      )
      .eq('account_id', accountId)
      .eq('id', taskId)
      .maybeSingle(),
    supabase
      .from('delivery_task_events')
      .select('id, event, data, latitude, longitude, created_at')
      .eq('account_id', accountId)
      .eq('task_id', taskId)
      .order('created_at', { ascending: false })
      .limit(100),
    supabase
      .from('proof_of_delivery')
      .select('recipient_name, age_verified, id_checked_type, id_last4, created_at')
      .eq('account_id', accountId)
      .eq('task_id', taskId)
      .maybeSingle(),
  ]);

  if (task.error) throw task.error;
  if (events.error) throw events.error;

  return { task: task.data, events: events.data ?? [], proof: proof.data ?? null };
}

function DeliveryDetailPage() {
  const { deliveryId } = useParams({ from: '/deliveries/$deliveryId' });
  const { activeAccountId } = useAuth();
  const accountId = activeAccountId ?? '';

  const timeZone = useQuery({
    queryKey: ['store-timezone', accountId],
    queryFn: () => getStoreTimeZone(accountId),
    enabled: Boolean(accountId),
    staleTime: Number.POSITIVE_INFINITY,
  });

  const zone = timeZone.data ?? FALLBACK_TIMEZONE;

  const detail = useQuery({
    queryKey: ['delivery', accountId, deliveryId],
    queryFn: () => loadTask(accountId, deliveryId),
    enabled: Boolean(accountId),
  });

  const task = detail.data?.task;
  const order = Array.isArray(task?.order) ? task?.order[0] : task?.order;

  useDetailTitle(
    order ? `Delivery · Order #${order.order_number}` : `Delivery ${deliveryId.slice(0, 8)}`,
  );

  if (detail.isLoading) {
    return (
      <div className="flex max-w-3xl flex-col gap-4">
        <Skeleton className="h-9 w-64" />
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!task) {
    return (
      <div className="text-muted-foreground flex flex-col items-center gap-3 py-24 text-center">
        <Truck className="size-8" />
        <p className="text-foreground text-sm font-medium">No such delivery</p>
        <p className="max-w-sm text-sm">
          It may have been deleted, or it belongs to another account.
        </p>
        <Button asChild variant="outline" size="sm">
          <Link to="/deliveries">
            <ArrowLeft className="size-4" /> Back to deliveries
          </Link>
        </Button>
      </div>
    );
  }

  const meta = TASK_STATUS_META[task.status as TaskStatus];
  const driver = Array.isArray(task.drivers) ? task.drivers[0] : task.drivers;
  const customer = Array.isArray(order?.customers) ? order?.customers[0] : order?.customers;
  const snapshot = (order?.customer_snapshot ?? {}) as Record<string, unknown>;
  const customerName =
    [customer?.first_name, customer?.last_name].filter(Boolean).join(' ') ||
    [snapshot.first_name, snapshot.last_name].filter(Boolean).join(' ') ||
    'Guest';

  return (
    <div className="flex max-w-3xl flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-semibold tracking-tight">
            {order ? `Order #${order.order_number}` : 'Delivery'}
          </h1>
          <Badge className={meta.className}>{meta.label}</Badge>
        </div>

        {order && (
          <Button asChild variant="outline" size="sm">
            <Link to="/orders/$orderId" params={{ orderId: order.id }}>
              Open order
            </Link>
          </Button>
        )}
      </div>

      <Section title="Task">
        <dl className="grid gap-3 text-sm sm:grid-cols-2">
          <Row label="Customer" value={customerName} />
          <Row
            label="Phone"
            value={customer?.phone ?? (snapshot.phone as string | undefined) ?? '—'}
          />
          <Row label="Address" value={formatAddress(task.destination_address) || 'No address'} />
          <Row label="Driver" value={driver?.name ?? 'Unassigned'} />
          <Row label="Created" value={formatStoreDateTime(task.created_at, zone)} />
          <Row label="ETA" value={formatStoreDateTime(task.eta, zone)} />
          <Row label="Started" value={formatStoreDateTime(task.started_at, zone)} />
          <Row label="Arrived" value={formatStoreDateTime(task.arrived_at, zone)} />
          <Row label="Completed" value={formatStoreDateTime(task.completed_at, zone)} />
          {task.failed_at && (
            <Row
              label="Failed"
              value={`${formatStoreDateTime(task.failed_at, zone)} — ${task.failure_reason ?? 'no reason'}`}
            />
          )}
        </dl>
      </Section>

      <Section title="Proof of delivery">
        {detail.data?.proof ? (
          <dl className="grid gap-3 text-sm sm:grid-cols-2">
            <Row label="Received by" value={detail.data.proof.recipient_name ?? '—'} />
            <Row label="Age verified" value={detail.data.proof.age_verified ? 'Yes' : 'No'} />
            <Row
              label="ID checked"
              value={
                detail.data.proof.id_checked_type
                  ? `${detail.data.proof.id_checked_type}${detail.data.proof.id_last4 ? ` ···${detail.data.proof.id_last4}` : ''}`
                  : '—'
              }
            />
            <Row label="Recorded" value={formatStoreDateTime(detail.data.proof.created_at, zone)} />
          </dl>
        ) : (
          <p className="text-muted-foreground text-sm">
            None recorded. Proof is captured by a driver app, which this build does not include; the
            dispatcher marks the hand-off from the board.
          </p>
        )}
      </Section>

      <Section title="Timeline">
        {detail.data?.events.length === 0 ? (
          <p className="text-muted-foreground text-sm">No events yet.</p>
        ) : (
          <ol className="flex flex-col gap-4">
            {detail.data?.events.map((event) => (
              <li key={event.id} className="flex gap-3">
                <span className="bg-primary/10 mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full">
                  <span className="bg-primary size-1.5 rounded-full" />
                </span>
                <div className="min-w-0">
                  <p className="text-sm capitalize">{event.event.replace(/_/g, ' ')}</p>
                  <p className="text-muted-foreground text-xs">
                    {formatRelative(event.created_at, zone)}
                    {typeof (event.data as Record<string, unknown> | null)?.reason === 'string'
                      ? ` · ${(event.data as Record<string, string>).reason}`
                      : ''}
                  </p>
                </div>
              </li>
            ))}
          </ol>
        )}
      </Section>
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="bg-card text-card-foreground flex flex-col gap-4 rounded-lg border p-5">
      <h2 className="text-sm font-medium">{title}</h2>
      {children}
    </section>
  );
}

function Row({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-muted-foreground text-xs">{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}
